import { EDatasourceType, IDatasourceDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEntitySwitcher, {
  KuikaEntitySwitcherGroup,
  KuikaEntitySwitcherItem
} from "../../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import {
  canManageDatasources,
  canRefreshDatasource,
  deleteDatasourceFromRow,
  refreshThirdPartyDatasource
} from "../datasource-row-actions";
import { isManagedDatasource } from "../datasources-landing-model";
import { getEditorGlyph, IEditorGlyph } from "./editor-glyph";
import { EditorGlyphIcon } from "./editor-glyph-icon";
import { buildConnectionSwitcherItems, getConnectionGroup } from "./editor-switcher-items";
import { ISwitcherDelete, useSwitcherDelete } from "./use-switcher-delete";

/**
 * The identity chip on a connection editor's bar: which connection is open, and the way to
 * any other one.
 *
 * Switching never opens the target directly. It PARKS the open as the editor's pending
 * close callback, exactly as the left tree and the landing page do, and the editor's own
 * `componentDidUpdate` then runs its guarded close — which offers to save first when the
 * form is dirty. Opening straight away would throw the edits away without asking.
 */

export interface IConnectionSwitcherChipProps {
  sd: IScreenDesignContextState;
  /** The editor's redux setter. The callback is read back by its `componentDidUpdate`. */
  setNextCallbackFunction: (callback?: () => void) => void;
  current: IDatasourceDto | undefined;
  /** The open connection's name — the editor's own live copy, which a rename changes. */
  name: string;
  /**
   * The connection's type, known before the record exists — it picks the glyph, its family
   * colour and the tooltip naming the connector ("PostgreSQL", "REST API").
   */
  datasourceType: EDatasourceType;
  triggerTestId?: string;
  /** The editor's guarded close — it leads to the datasources list. */
  onManageAll: () => void;
}

/** The landing's own create cards — where a connection's connector sits. */
const CONNECTION_GROUPS: KuikaEntitySwitcherGroup[] = [
  { key: "database", label: "Database" },
  { key: "api", label: "API" },
  { key: "function", label: "Function" }
];

export const ConnectionSwitcherChip = ({
  sd,
  setNextCallbackFunction,
  current,
  name,
  datasourceType,
  triggerTestId,
  onManageAll
}: IConnectionSwitcherChipProps): React.ReactElement => {
  const glyph: IEditorGlyph = getEditorGlyph({ kind: "datasource", datasourceType });
  const items: KuikaEntitySwitcherItem[] = buildConnectionSwitcherItems(sd.datasources ?? []);
  const find = (id: string): IDatasourceDto | undefined =>
    (sd.datasources ?? []).find((datasource: IDatasourceDto): boolean => datasource.id === id);
  const { deleteOption, deleteModal }: ISwitcherDelete<IDatasourceDto> = useSwitcherDelete(
    "datasource",
    (datasource: IDatasourceDto): void => void deleteDatasourceFromRow(sd, datasource)
  );

  // The datasources list's own rules for a datasource row.
  const mayManage: boolean = canManageDatasources(sd);

  return (
    <>
      <KuikaEntitySwitcher
        trigger="chip"
        triggerIcon={<EditorGlyphIcon glyph={glyph} />}
        triggerIconTooltip={glyph.label}
        triggerLabel={name}
        triggerTestId={triggerTestId}
        items={items}
        current={current ? { id: current.id, name } : undefined}
        entityLabel="Connection"
        iconName="database"
        iconType="datasource-icons"
        onSelect={(item: KuikaEntitySwitcherItem): void => {
          const target: IDatasourceDto | undefined = (sd.datasources ?? []).find(
            (datasource: IDatasourceDto): boolean => datasource.id === item.id
          );
          if (!target) return;
          setNextCallbackFunction((): void => sd.setActiveEditingDatasource(target));
        }}
        onCreateNew={
          mayManage
            ? (): void => {
                // Creating picks a connector first, and that screen is the landing page's, so the
                // editor closes to it rather than growing a second copy of the catalog.
                setNextCallbackFunction((): void => sd.requestDatasourceCreate("all"));
              }
            : undefined
        }
        itemTestId={(item: KuikaEntitySwitcherItem): string => `${item.name.replace(/\s/g, "")}SwitcherItem`}
        groups={CONNECTION_GROUPS}
        getItemGroup={(item: KuikaEntitySwitcherItem): string => {
          const datasource: IDatasourceDto | undefined = find(item.id);
          return datasource ? getConnectionGroup(datasource) : "database";
        }}
        onManageAll={onManageAll}
        getRowActions={(item: KuikaEntitySwitcherItem): KuikaDropdownMenuItemType[] => {
          const datasource: IDatasourceDto | undefined = find(item.id);
          if (!datasource) return [];
          return [
            {
              key: "refresh",
              label: "Refresh",
              icon: <KuikaSvg name="refresh" width={16} height={16} />,
              hidden: !mayManage || !canRefreshDatasource(datasource),
              onClick: (): void => void refreshThirdPartyDatasource(sd, datasource)
            },
            deleteOption(datasource, !mayManage || isManagedDatasource(datasource))
          ];
        }}
      />
      {deleteModal}
    </>
  );
};
